import React, { useEffect } from "react";
import { toast } from "react-toastify";

import { SubStatusModel } from "../../../../factories/statuses/models/status.model";
import {
  useAddSubStatusMutation,
  useUpdateSubStatusMutation,
} from "../../../../slices/StatusesSlice";
import { SettingsFormInput } from "../../components/SettingsFormInput";
import { serverErrorMessage } from "../../workTypesV2/model/workTypeEditor.utils";

type Props = {
  subStatus?: SubStatusModel;
  onClose: () => void;
  onSuccess: () => void;
  workTypeStatusId?: string;
};

// The server's own sentence for a 400 (e.g. a duplicate name); never a 500's
// technical text.
const getStatusMutationErrorMessage = (error: unknown) =>
  serverErrorMessage(error, "Something went wrong");

export const StatusForm: React.FC<Props> = ({
  subStatus,
  onClose,
  onSuccess,
  workTypeStatusId,
}) => {
  const defaultValue = subStatus?.subStatus || "";

  const [
    updateSubStatus,
    {
      isLoading: isLoadingUpdate,
      isSuccess: isSuccessUpdate,
      isError: isErrorUpdate,
      error: updateError,
    },
  ] = useUpdateSubStatusMutation();
  const [
    addSubStatus,
    {
      isLoading: isLoadingAdd,
      isSuccess: isSuccessAdd,
      isError: isErrorAdd,
      error: addError,
    },
  ] = useAddSubStatusMutation();

  const onSubmit = async (value: string) => {
    if (!value.trim()) {
      return;
    }

    if (subStatus) {
      await updateSubStatus({ ...subStatus, subStatus: value });
      return;
    }

    if (workTypeStatusId) {
      await addSubStatus({
        subStatus: value,
        isChecked: false,
        workTypeStatusId,
        serviceIds: [],
      });
    }
  };

  useEffect(() => {
    if (isSuccessUpdate) {
      toast("Status successfully Updated", {
        type: "success",
      });
      onSuccess();
      onClose();
    }
  }, [isSuccessUpdate, onClose, onSuccess]);

  useEffect(() => {
    if (isSuccessAdd) {
      toast("Status successfully Created", {
        type: "success",
      });
      onSuccess();
      onClose();
    }
  }, [isSuccessAdd, onClose, onSuccess]);

  useEffect(() => {
    if (isErrorUpdate) {
      toast(getStatusMutationErrorMessage(updateError), {
        type: "error",
      });
    }
  }, [isErrorUpdate, updateError]);

  useEffect(() => {
    if (isErrorAdd) {
      toast(getStatusMutationErrorMessage(addError), {
        type: "error",
      });
    }
  }, [isErrorAdd, addError]);

  return (
    <SettingsFormInput
      onSave={onSubmit}
      onCancel={onClose}
      isLoading={isLoadingUpdate || isLoadingAdd}
      defaultValue={defaultValue}
      placeholder={"Type status name..."}
    />
  );
};
